import React, { useState, useEffect, useCallback } from 'react';
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import Auth from '../Function/Auth';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import { canModuleEdit } from '../Function/Permission';

function Rewards() {
  const authToken = sessionStorage.getItem('authToken');
  const rawBaseUrl = process.env.REACT_APP_BASE_API_URL || 'https://api.marclife.io/api/admin';
  const baseUrl = rawBaseUrl.endsWith('/') ? rawBaseUrl.slice(0, -1) : rawBaseUrl;

  // Sub-Admin Permission: can this admin edit rewards?
  const canEdit = canModuleEdit('rewards');

  // State Management
  const [rewardsList, setRewardsList] = useState([]);
  const [analytics, setAnalytics] = useState({
    total_rewards: 0,
    active_rewards: 0,
    closed_rewards: 0,
    total_budget: 0
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'

  // Filters
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'closed'
  const [searchQuery, setSearchQuery] = useState('');

  // Modals & Active Reward
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [selectedReward, setSelectedReward] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [rewardToDelete, setRewardToDelete] = useState(null);
  const [previewImageModal, setPreviewImageModal] = useState(null);

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    reward_name: '',
    reward_amount: '',
    from_date: '',
    to_date: '',
    self_stake: '',
    self_stake_status: '1',
    direct_business: '',
    direct_business_status: '1',
    team_business: '',
    team_business_status: '1',
    status: 'active'
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');

  // Fetch Rewards from API
  const fetchRewards = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchQuery.trim()) params.search = searchQuery.trim();

      const response = await axios.get(`${baseUrl}/rewardData`, {
        params,
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      if (response.data && response.data.status) {
        const data = response.data.data || [];
        setRewardsList(data);

        if (response.data.analytics) {
          setAnalytics(response.data.analytics);
        } else {
          // Fallback calculation
          setAnalytics({
            total_rewards: data.length,
            active_rewards: data.filter(r => r.status === 'active').length,
            closed_rewards: data.filter(r => r.status !== 'active').length,
            total_budget: data.reduce((acc, curr) => acc + (parseFloat(curr.reward_amount) || 0), 0)
          });
        }
      } else {
        setRewardsList([]);
      }
    } catch (err) {
      console.error('Error fetching rewards:', err);
      // If 404 is returned because table is empty, handle gracefully
      if (err.response && err.response.status === 404) {
        setRewardsList([]);
      } else {
        toast.error(err.response?.data?.message || 'Failed to load promotional reward targets');
      }
    } finally {
      setLoading(false);
    }
  }, [baseUrl, authToken, statusFilter, searchQuery]);

  useEffect(() => {
    fetchRewards();
  }, [fetchRewards]);

  // Open Create Modal
  const handleOpenCreate = () => {
    if (!canEdit) {
      return toast.warn('Permission Denied: Your admin role only has view access for Rewards.');
    }
    setModalMode('create');
    setSelectedReward(null);
    setFormData({
      name: '',
      reward_name: '',
      reward_amount: '',
      from_date: new Date().toISOString().split('T')[0],
      to_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      self_stake: '0',
      self_stake_status: '1',
      direct_business: '0',
      direct_business_status: '1',
      team_business: '0',
      team_business_status: '1',
      status: 'active'
    });
    setImageFile(null);
    setImagePreview('');
    setShowModal(true);
  };

  // Open Edit Modal
  const handleOpenEdit = (reward) => {
    if (!canEdit) {
      return toast.warn('Permission Denied: Your admin role only has view access for Rewards.');
    }
    setModalMode('edit');
    setSelectedReward(reward);
    setFormData({
      name: reward.name || '',
      reward_name: reward.reward_name || '',
      reward_amount: reward.reward_amount || '0',
      from_date: reward.from_date ? reward.from_date.substring(0, 10) : '',
      to_date: reward.to_date ? reward.to_date.substring(0, 10) : '',
      self_stake: reward.self_stake || '0',
      self_stake_status: reward.self_stake_status ? String(reward.self_stake_status) : '1',
      direct_business: reward.direct_business || '0',
      direct_business_status: reward.direct_business_status ? String(reward.direct_business_status) : '1',
      team_business: reward.team_business || '0',
      team_business_status: reward.team_business_status ? String(reward.team_business_status) : '1',
      status: reward.status || 'active'
    });
    setImageFile(null);
    setImagePreview(reward.image || '');
    setShowModal(true);
  };

  // Handle Image Change with preview
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        return toast.error('Image size must be less than 5MB');
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Save / Submit Form
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    if (!canEdit) {
      return toast.error('Permission Denied: Your admin role only has view access.');
    }

    if (!formData.name.trim()) return toast.error('Campaign Target Title is required');
    if (!formData.reward_name.trim()) return toast.error('Reward Benefit Name is required');
    if (!formData.from_date || !formData.to_date) return toast.error('Valid Date range is required');

    setSubmitting(true);
    try {
      const formPayload = new FormData();
      formPayload.append('name', formData.name.trim());
      formPayload.append('reward_name', formData.reward_name.trim());
      formPayload.append('reward_amount', formData.reward_amount || '0');
      formPayload.append('from_date', formData.from_date);
      formPayload.append('to_date', formData.to_date);
      formPayload.append('self_stake', formData.self_stake || '0');
      formPayload.append('self_stake_status', formData.self_stake_status);
      formPayload.append('direct_business', formData.direct_business || '0');
      formPayload.append('direct_business_status', formData.direct_business_status);
      formPayload.append('team_business', formData.team_business || '0');
      formPayload.append('team_business_status', formData.team_business_status);
      formPayload.append('status', formData.status);

      if (imageFile) {
        formPayload.append('image', imageFile);
      }

      let response;
      if (modalMode === 'create') {
        response = await axios.post(`${baseUrl}/createReward`, formPayload, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        response = await axios.post(`${baseUrl}/updateReward/${selectedReward.id}`, formPayload, {
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      if (response.data && response.data.status) {
        toast.success(response.data.message || (modalMode === 'create' ? 'Reward created!' : 'Reward updated!'));
        setShowModal(false);
        fetchRewards();
      } else {
        toast.error(response.data?.message || 'Operation failed');
      }
    } catch (err) {
      console.error('Error submitting reward form:', err);
      toast.error(err.response?.data?.message || (err.response?.data?.data ? JSON.stringify(err.response.data.data) : 'Failed to save reward target'));
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle Status (active <-> closed)
  const handleToggleStatus = async (reward) => {
    if (!canEdit) {
      return toast.warn('Permission Denied: Viewer admin cannot change target status.');
    }
    const newStatus = reward.status === 'active' ? 'closed' : 'active';
    try {
      const response = await axios.post(`${baseUrl}/updateReward/${reward.id}`, {
        status: newStatus
      }, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      if (response.data && response.data.status) {
        toast.success(`Target status changed to ${newStatus.toUpperCase()}`);
        fetchRewards();
      } else {
        toast.error(response.data?.message || 'Failed to update status');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to toggle status');
    }
  };

  // Confirm Delete
  const handleConfirmDelete = (reward) => {
    if (!canEdit) {
      return toast.warn('Permission Denied: Viewer admin cannot delete targets.');
    }
    setRewardToDelete(reward);
    setShowDeleteModal(true);
  };

  // Execute Delete
  const handleDeleteReward = async () => {
    if (!rewardToDelete) return;
    setSubmitting(true);
    try {
      const response = await axios.post(`${baseUrl}/deleteReward/${rewardToDelete.id}`, {}, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });

      if (response.data && response.data.status) {
        toast.success('Reward target deleted successfully!');
        setShowDeleteModal(false);
        setRewardToDelete(null);
        fetchRewards();
      } else {
        toast.error(response.data?.message || 'Failed to delete target');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Error deleting reward target');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper to format currency
  const formatCurrency = (val) => {
    return parseFloat(val || 0).toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };

  // Helper to calculate days remaining
  const calculateDaysRemaining = (toDateStr) => {
    if (!toDateStr) return null;
    const end = new Date(toDateStr);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <>
      <Header>
        <Auth />
        <div className="main-content app-content">
          <div className="container-fluid">
            {/* Page Header */}
            <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
              <div>
                <h4 className="page-title fw-bold text-dark mb-1 d-flex align-items-center gap-2">
                  <i className="fa fa-gift text-primary fs-22"></i>
                  Promotional Rewards & Target Offers
                  {!canEdit && (
                    <span className="badge bg-warning-subtle text-warning border border-warning-subtle fs-11 fw-normal ms-2">
                      <i className="fa fa-eye me-1"></i> Viewer Mode (Read-Only)
                    </span>
                  )}
                </h4>
                <p className="text-muted fs-13 mb-0">
                  Configure time-bound campaign targets for users &middot; Tours, Luxury Cars, Cash Rewards & Team Business targets.
                </p>
              </div>

              <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 shadow-sm"
                  onClick={fetchRewards}
                  disabled={loading}
                >
                  <i className={`fa fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
                  <span>Refresh</span>
                </button>

                {canEdit ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-primary d-flex align-items-center gap-2 px-3 shadow-sm fw-semibold"
                    onClick={handleOpenCreate}
                  >
                    <i className="fa fa-plus-circle"></i>
                    <span>Create New Target Offer</span>
                  </button>
                ) : (
                  <span className="badge bg-light text-muted border p-2">
                    <i className="fa fa-lock me-1"></i> Sub-Admin View Only
                  </span>
                )}
              </div>
            </div>

            {/* Read-Only Notice for Sub-Admins */}
            {!canEdit && (
              <div className="alert alert-warning border-0 shadow-sm d-flex align-items-center p-3 mb-4 rounded-3">
                <i className="fa fa-shield-alt text-warning fs-24 me-3"></i>
                <div>
                  <h6 className="alert-heading fw-bold mb-1">Sub-Admin Read-Only Access</h6>
                  <p className="mb-0 fs-12 text-muted">
                    Your account has viewer permissions for the Rewards module. You can browse active campaigns, review targets, and preview flyers, but creating, editing, and deleting rewards is restricted.
                  </p>
                </div>
              </div>
            )}

            {/* Analytics KPI Row */}
            <div className="row g-3 mb-4">
              {/* Total Rewards */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-12">
                <div className="card border-0 shadow-sm rounded-3 h-100">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-muted fs-11 text-uppercase fw-semibold">Total Campaigns</span>
                        <h3 className="fw-bold mb-0 text-dark mt-1 font-monospace">{analytics.total_rewards}</h3>
                        <span className="text-muted fs-11">All-Time Targets Configured</span>
                      </div>
                      <div className="p-3 bg-primary-subtle text-primary rounded-3">
                        <i className="fa fa-trophy fs-24"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Targets */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-12">
                <div className="card border-0 shadow-sm rounded-3 h-100 border-start border-4 border-success">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-muted fs-11 text-uppercase fw-semibold">Active Targets</span>
                        <h3 className="fw-bold mb-0 text-success mt-1 font-monospace">
                          {analytics.active_rewards}
                        </h3>
                        <span className="text-success fs-11 fw-semibold">
                          <i className="fa fa-circle fs-8 me-1 text-success"></i> Live for User Qualification
                        </span>
                      </div>
                      <div className="p-3 bg-success-subtle text-success rounded-3">
                        <i className="fa fa-plane-departure fs-24"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Closed Targets */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-12">
                <div className="card border-0 shadow-sm rounded-3 h-100">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-muted fs-11 text-uppercase fw-semibold">Closed / Concluded</span>
                        <h3 className="fw-bold mb-0 text-secondary mt-1 font-monospace">
                          {analytics.closed_rewards}
                        </h3>
                        <span className="text-muted fs-11">Expired Campaign Targets</span>
                      </div>
                      <div className="p-3 bg-secondary-subtle text-secondary rounded-3">
                        <i className="fa fa-flag-checkered fs-24"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Reward Budget */}
              <div className="col-xl-3 col-lg-6 col-md-6 col-12">
                <div className="card border-0 shadow-sm rounded-3 h-100">
                  <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="text-muted fs-11 text-uppercase fw-semibold">Total Reward Pool</span>
                        <h3 className="fw-bold mb-0 text-info mt-1 font-monospace">
                          ${formatCurrency(analytics.total_budget)}
                        </h3>
                        <span className="text-muted fs-11">Cumulative Offer Value</span>
                      </div>
                      <div className="p-3 bg-info-subtle text-info rounded-3">
                        <i className="fa fa-money-bill-wave fs-24"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Filter and Control Bar */}
            <div className="card border-0 shadow-sm rounded-3 mb-4">
              <div className="card-body p-3">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                  {/* Status Filter Pills */}
                  <div className="d-flex align-items-center gap-1">
                    <button
                      type="button"
                      className={`btn btn-sm ${statusFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'} rounded-pill px-3`}
                      onClick={() => setStatusFilter('all')}
                    >
                      All Offers ({rewardsList.length})
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${statusFilter === 'active' ? 'btn-success' : 'btn-outline-secondary'} rounded-pill px-3`}
                      onClick={() => setStatusFilter('active')}
                    >
                      <i className="fa fa-check-circle me-1"></i> Active
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${statusFilter === 'closed' ? 'btn-secondary' : 'btn-outline-secondary'} rounded-pill px-3`}
                      onClick={() => setStatusFilter('closed')}
                    >
                      <i className="fa fa-times-circle me-1"></i> Closed
                    </button>
                  </div>

                  {/* Search and Layout Toggle */}
                  <div className="d-flex align-items-center gap-2">
                    <div className="input-group input-group-sm" style={{ width: '260px' }}>
                      <span className="input-group-text bg-light border-end-0">
                        <i className="fa fa-search text-muted fs-12"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control border-start-0"
                        placeholder="Search target or tour..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      {searchQuery && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setSearchQuery('')}
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      )}
                    </div>

                    <div className="btn-group btn-group-sm" role="group">
                      <button
                        type="button"
                        className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setViewMode('grid')}
                        title="Card Grid View"
                      >
                        <i className="fa fa-th-large"></i>
                      </button>
                      <button
                        type="button"
                        className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setViewMode('table')}
                        title="Detailed Table View"
                      >
                        <i className="fa fa-list"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="card border-0 shadow-sm rounded-3 py-5 text-center">
                <div className="spinner-border text-primary mx-auto mb-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <h6 className="text-dark fw-semibold">Loading Promotional Reward Targets...</h6>
                <p className="text-muted fs-13 mb-0">Retrieving offer targets and campaigns from server</p>
              </div>
            ) : rewardsList.length === 0 ? (
              /* Empty State */
              <div className="card border-0 shadow-sm rounded-3 py-5 text-center">
                <div className="p-4 rounded-circle bg-light d-inline-block mx-auto mb-3">
                  <i className="fa fa-gift fs-40 text-muted"></i>
                </div>
                <h5 className="fw-bold text-dark mb-1">No Promotional Reward Targets Found</h5>
                <p className="text-muted fs-13 mb-3">
                  {searchQuery || statusFilter !== 'all'
                    ? 'No campaigns match your selected filter criteria. Try clearing search filters.'
                    : 'Create your first promotional target offer to motivate community members and users with tours, gadgets, or bonuses.'}
                </p>
                {canEdit && (
                  <button
                    type="button"
                    className="btn btn-sm btn-primary mx-auto px-4"
                    onClick={handleOpenCreate}
                  >
                    <i className="fa fa-plus me-1"></i> Create First Target Offer
                  </button>
                )}
              </div>
            ) : viewMode === 'grid' ? (
              /* GRID VIEW */
              <div className="row g-4 mb-4">
                {rewardsList.map((reward) => {
                  const daysLeft = calculateDaysRemaining(reward.to_date);
                  const isClosed = reward.status !== 'active';

                  return (
                    <div key={reward.id} className="col-xl-4 col-lg-6 col-md-6 col-12">
                      <div className="card border-0 shadow-sm rounded-3 h-100 overflow-hidden d-flex flex-column">
                        {/* Image Flyer Header */}
                        <div
                          className="position-relative bg-dark"
                          style={{ height: '180px', overflow: 'hidden' }}
                        >
                          {reward.image ? (
                            <img
                              src={reward.image}
                              alt={reward.name}
                              className="w-100 h-100 object-fit-cover"
                              style={{ cursor: 'pointer', transition: 'transform 0.3s ease' }}
                              onClick={() => setPreviewImageModal(reward.image)}
                              title="Click to view full image"
                            />
                          ) : (
                            <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center bg-primary-gradient text-white">
                              <i className="fa fa-gift fs-48 mb-2 opacity-75"></i>
                              <span className="fs-12 fw-semibold text-uppercase tracking-wider">MarcLife Rewards</span>
                            </div>
                          )}

                          {/* Top Badges */}
                          <div className="position-absolute top-0 start-0 p-2 d-flex gap-1">
                            <span className={`badge ${!isClosed ? 'bg-success' : 'bg-secondary'} shadow-sm text-uppercase fs-11`}>
                              {!isClosed ? (
                                <>
                                  <i className="fa fa-circle me-1 fs-8"></i> Active Target
                                </>
                              ) : (
                                <>
                                  <i className="fa fa-ban me-1"></i> Closed
                                </>
                              )}
                            </span>
                          </div>

                          <div className="position-absolute top-0 end-0 p-2">
                            {daysLeft !== null && !isClosed && (
                              <span className={`badge ${daysLeft > 7 ? 'bg-info' : daysLeft > 0 ? 'bg-warning text-dark' : 'bg-danger'} shadow-sm fs-11`}>
                                <i className="fa fa-clock me-1"></i>
                                {daysLeft > 0 ? `${daysLeft} Days Remaining` : 'Expired Today'}
                              </span>
                            )}
                          </div>

                          {/* Reward Name Banner */}
                          <div
                            className="position-absolute bottom-0 start-0 w-100 p-2 text-white"
                            style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.85))' }}
                          >
                            <h6 className="fw-bold mb-0 text-white text-truncate">{reward.name}</h6>
                            <span className="fs-12 text-warning fw-semibold">
                              <i className="fa fa-award me-1"></i> {reward.reward_name}
                            </span>
                          </div>
                        </div>

                        {/* Card Body with Requirements */}
                        <div className="card-body p-3 flex-grow-1 d-flex flex-column justify-content-between">
                          <div>
                            {/* Reward Value Bar */}
                            <div className="d-flex align-items-center justify-content-between p-2 mb-3 bg-light rounded-2 border">
                              <div>
                                <span className="text-muted fs-11 text-uppercase">Reward Value / Prize:</span>
                                <div className="fw-bold text-dark fs-14">
                                  ${formatCurrency(reward.reward_amount)}
                                </div>
                              </div>
                              <span className="badge bg-primary-subtle text-primary border border-primary-subtle fs-12 px-2 py-1">
                                <i className="fa fa-gift me-1"></i> Prize Pool
                              </span>
                            </div>

                            {/* Eligibility Target Thresholds */}
                            <div className="mb-3">
                              <span className="text-muted fs-11 text-uppercase fw-semibold d-block mb-2">
                                Qualification Target Rules:
                              </span>

                              <div className="row g-2 fs-12">
                                {/* Self Stake */}
                                <div className="col-12">
                                  <div className="p-2 border rounded-2 bg-white d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center gap-2">
                                      <i className="fa fa-wallet text-primary"></i>
                                      <span className="text-muted">Self Stake:</span>
                                    </div>
                                    <div className="fw-bold font-monospace text-dark">
                                      ${formatCurrency(reward.self_stake)}
                                      {reward.self_stake_status === '1' || reward.self_stake_status === 'active' ? (
                                        <span className="badge bg-success-subtle text-success fs-9 ms-1">Required</span>
                                      ) : (
                                        <span className="badge bg-light text-muted fs-9 ms-1">Optional</span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Direct Business */}
                                <div className="col-12">
                                  <div className="p-2 border rounded-2 bg-white d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center gap-2">
                                      <i className="fa fa-user-plus text-success"></i>
                                      <span className="text-muted">Direct Business:</span>
                                    </div>
                                    <div className="fw-bold font-monospace text-dark">
                                      ${formatCurrency(reward.direct_business)}
                                      {reward.direct_business_status === '1' || reward.direct_business_status === 'active' ? (
                                        <span className="badge bg-success-subtle text-success fs-9 ms-1">Required</span>
                                      ) : (
                                        <span className="badge bg-light text-muted fs-9 ms-1">Optional</span>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Team Business */}
                                <div className="col-12">
                                  <div className="p-2 border rounded-2 bg-white d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center gap-2">
                                      <i className="fa fa-users text-info"></i>
                                      <span className="text-muted">Team Business:</span>
                                    </div>
                                    <div className="fw-bold font-monospace text-dark">
                                      ${formatCurrency(reward.team_business)}
                                      {reward.team_business_status === '1' || reward.team_business_status === 'active' ? (
                                        <span className="badge bg-success-subtle text-success fs-9 ms-1">Required</span>
                                      ) : (
                                        <span className="badge bg-light text-muted fs-9 ms-1">Optional</span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Timeline Date Range */}
                            <div className="d-flex align-items-center justify-content-between text-muted fs-11 p-2 bg-light rounded-2 border mb-3">
                              <div>
                                <i className="fa fa-calendar-alt me-1 text-primary"></i>
                                <strong>Start:</strong> {reward.from_date ? reward.from_date.substring(0, 10) : 'N/A'}
                              </div>
                              <div>
                                <i className="fa fa-calendar-check me-1 text-danger"></i>
                                <strong>End:</strong> {reward.to_date ? reward.to_date.substring(0, 10) : 'N/A'}
                              </div>
                            </div>
                          </div>

                          {/* Footer Action Buttons */}
                          <div className="pt-2 border-top d-flex align-items-center justify-content-between gap-2">
                            {canEdit ? (
                              <>
                                <button
                                  type="button"
                                  className={`btn btn-sm ${!isClosed ? 'btn-outline-warning' : 'btn-outline-success'} flex-fill`}
                                  onClick={() => handleToggleStatus(reward)}
                                  title={!isClosed ? 'Close Target' : 'Activate Target'}
                                >
                                  <i className={`fa ${!isClosed ? 'fa-pause' : 'fa-play'} me-1`}></i>
                                  {!isClosed ? 'Close Target' : 'Activate Target'}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => handleOpenEdit(reward)}
                                  title="Edit Target"
                                >
                                  <i className="fa fa-edit"></i>
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleConfirmDelete(reward)}
                                  title="Delete Target"
                                >
                                  <i className="fa fa-trash-alt"></i>
                                </button>
                              </>
                            ) : (
                              <span className="text-muted fs-11 fst-italic w-100 text-center">
                                <i className="fa fa-eye me-1"></i> Read-Only View
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* TABLE VIEW */
              <div className="card border-0 shadow-sm rounded-3 mb-4 overflow-hidden">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0 text-nowrap">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '60px' }}>ID</th>
                        <th>Target Campaign</th>
                        <th>Reward Benefit</th>
                        <th>Reward Value</th>
                        <th>Direct Target</th>
                        <th>Team Target</th>
                        <th>Valid Period</th>
                        <th>Status</th>
                        <th className="text-end" style={{ width: '140px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rewardsList.map((reward) => (
                        <tr key={reward.id}>
                          <td>
                            <span className="badge bg-light text-dark font-monospace border">#{reward.id}</span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {reward.image ? (
                                <img
                                  src={reward.image}
                                  alt={reward.name}
                                  width={40}
                                  height={40}
                                  className="rounded border object-fit-cover"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => setPreviewImageModal(reward.image)}
                                />
                              ) : (
                                <div className="rounded bg-light border d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                                  <i className="fa fa-gift text-primary"></i>
                                </div>
                              )}
                              <div>
                                <h6 className="fw-bold mb-0 text-dark fs-13">{reward.name}</h6>
                                <span className="text-muted fs-11">{reward.reward_name}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="badge bg-primary-subtle text-primary border border-primary-subtle">
                              <i className="fa fa-award me-1"></i> {reward.reward_name}
                            </span>
                          </td>
                          <td>
                            <span className="fw-bold font-monospace text-dark">
                              ${formatCurrency(reward.reward_amount)}
                            </span>
                          </td>
                          <td>
                            <div className="font-monospace fs-12">
                              ${formatCurrency(reward.direct_business)}
                            </div>
                          </td>
                          <td>
                            <div className="font-monospace fs-12">
                              ${formatCurrency(reward.team_business)}
                            </div>
                          </td>
                          <td>
                            <div className="fs-11 text-muted">
                              <div>{reward.from_date ? reward.from_date.substring(0, 10) : 'N/A'}</div>
                              <div className="fw-semibold text-dark">to {reward.to_date ? reward.to_date.substring(0, 10) : 'N/A'}</div>
                            </div>
                          </td>
                          <td>
                            <span className={`badge ${reward.status === 'active' ? 'bg-success-subtle text-success border border-success-subtle' : 'bg-secondary-subtle text-secondary border border-secondary-subtle'}`}>
                              {reward.status === 'active' ? 'Active' : 'Closed'}
                            </span>
                          </td>
                          <td className="text-end">
                            {canEdit ? (
                              <div className="btn-group btn-group-sm">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={() => handleToggleStatus(reward)}
                                  title={reward.status === 'active' ? 'Close Target' : 'Activate Target'}
                                >
                                  <i className={`fa ${reward.status === 'active' ? 'fa-pause' : 'fa-play'}`}></i>
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline-primary"
                                  onClick={() => handleOpenEdit(reward)}
                                  title="Edit"
                                >
                                  <i className="fa fa-edit"></i>
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger"
                                  onClick={() => handleConfirmDelete(reward)}
                                  title="Delete"
                                >
                                  <i className="fa fa-trash-alt"></i>
                                </button>
                              </div>
                            ) : (
                              <span className="badge bg-light text-muted border">
                                <i className="fa fa-eye"></i> View Only
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ==================================================== */}
        {/* CREATE / EDIT REWARD TARGET MODAL */}
        {/* ==================================================== */}
        {showModal && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content border-0 shadow-lg rounded-3">
                <div className="modal-header bg-white py-3 border-bottom">
                  <h6 className="modal-title fw-bold text-dark d-flex align-items-center gap-2">
                    <i className="fa fa-gift text-primary"></i>
                    {modalMode === 'create' ? 'Create New Promotional Reward Target' : `Edit Reward Target #${selectedReward?.id}`}
                  </h6>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowModal(false)}
                    disabled={submitting}
                  ></button>
                </div>

                <form onSubmit={handleSubmitForm}>
                  <div className="modal-body p-4">
                    {/* Basic Campaign Info */}
                    <div className="row g-3 mb-3">
                      <div className="col-md-6 col-12">
                        <label className="form-label fs-12 fw-semibold">
                          Campaign Target Title <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="e.g. Dubai Luxury Tour 2026 Target"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                        />
                      </div>

                      <div className="col-md-6 col-12">
                        <label className="form-label fs-12 fw-semibold">
                          Reward Benefit / Prize <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="e.g. Dubai 5D/4N Package or iPhone 16 Pro"
                          value={formData.reward_name}
                          onChange={(e) => setFormData({ ...formData, reward_name: e.target.value })}
                          required
                        />
                      </div>

                      <div className="col-md-6 col-12">
                        <label className="form-label fs-12 fw-semibold">
                          Reward Cash / Approximate Value ($/USDT)
                        </label>
                        <div className="input-group input-group-sm">
                          <span className="input-group-text bg-light">$</span>
                          <input
                            type="number"
                            step="any"
                            min="0"
                            className="form-control font-monospace"
                            placeholder="5000"
                            value={formData.reward_amount}
                            onChange={(e) => setFormData({ ...formData, reward_amount: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="col-md-6 col-12">
                        <label className="form-label fs-12 fw-semibold">
                          Campaign Status <span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-select form-select-sm"
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                          required
                        >
                          <option value="active">Active (Live for Users)</option>
                          <option value="closed">Closed / Expired</option>
                        </select>
                      </div>
                    </div>

                    {/* Image Upload / Flyer */}
                    <div className="mb-4 p-3 bg-light rounded-3 border">
                      <label className="form-label fs-12 fw-semibold d-flex align-items-center justify-content-between mb-2">
                        <span>
                          <i className="fa fa-image me-1 text-primary"></i> Promotional Flyer / Banner Image
                        </span>
                        <span className="text-muted fs-11">Recommended: 800x500 PNG / JPG (Max 5MB)</span>
                      </label>

                      <div className="row g-3 align-items-center">
                        <div className="col-md-8 col-12">
                          <input
                            type="file"
                            accept="image/*"
                            className="form-control form-control-sm"
                            onChange={handleImageChange}
                          />
                          <span className="text-muted fs-11 d-block mt-1">
                            Uploaded flyer will be saved to <code>public/images/rewards</code> on server.
                          </span>
                        </div>

                        {imagePreview && (
                          <div className="col-md-4 col-12 text-center">
                            <div className="position-relative d-inline-block border rounded p-1 bg-white shadow-sm">
                              <img
                                src={imagePreview}
                                alt="Preview"
                                style={{ maxHeight: '90px', maxWidth: '100%' }}
                                className="rounded object-fit-cover"
                              />
                              <button
                                type="button"
                                className="btn btn-xs btn-danger position-absolute top-0 end-0 rounded-circle py-0 px-1"
                                onClick={() => {
                                  setImageFile(null);
                                  setImagePreview('');
                                }}
                                title="Remove Image"
                              >
                                &times;
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Timeline Date Range */}
                    <div className="row g-3 mb-4">
                      <div className="col-md-6 col-12">
                        <label className="form-label fs-12 fw-semibold">
                          Target Start Date (From) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={formData.from_date}
                          onChange={(e) => setFormData({ ...formData, from_date: e.target.value })}
                          required
                        />
                      </div>

                      <div className="col-md-6 col-12">
                        <label className="form-label fs-12 fw-semibold">
                          Target End Date (To) <span className="text-danger">*</span>
                        </label>
                        <input
                          type="date"
                          className="form-control form-control-sm"
                          value={formData.to_date}
                          onChange={(e) => setFormData({ ...formData, to_date: e.target.value })}
                          required
                        />
                      </div>
                    </div>

                    {/* Qualification Target Rules */}
                    <div className="border rounded-3 p-3 bg-white">
                      <h6 className="fw-bold text-dark fs-13 mb-3 d-flex align-items-center gap-2">
                        <i className="fa fa-sliders-h text-primary"></i>
                        Qualification Target Thresholds ($/USDT)
                      </h6>

                      <div className="row g-3">
                        {/* Self Stake */}
                        <div className="col-md-4 col-12">
                          <label className="form-label fs-12 fw-semibold">Self Stake Required ($)</label>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            className="form-control form-control-sm font-monospace"
                            placeholder="0"
                            value={formData.self_stake}
                            onChange={(e) => setFormData({ ...formData, self_stake: e.target.value })}
                          />
                          <div className="form-check form-switch mt-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="selfStakeStatusCheck"
                              checked={formData.self_stake_status === '1' || formData.self_stake_status === 'active'}
                              onChange={(e) => setFormData({ ...formData, self_stake_status: e.target.checked ? '1' : '0' })}
                            />
                            <label className="form-check-label fs-11 text-muted" htmlFor="selfStakeStatusCheck">
                              Rule Mandatory
                            </label>
                          </div>
                        </div>

                        {/* Direct Business */}
                        <div className="col-md-4 col-12">
                          <label className="form-label fs-12 fw-semibold">Direct Business Required ($)</label>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            className="form-control form-control-sm font-monospace"
                            placeholder="0"
                            value={formData.direct_business}
                            onChange={(e) => setFormData({ ...formData, direct_business: e.target.value })}
                          />
                          <div className="form-check form-switch mt-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="directBusinessStatusCheck"
                              checked={formData.direct_business_status === '1' || formData.direct_business_status === 'active'}
                              onChange={(e) => setFormData({ ...formData, direct_business_status: e.target.checked ? '1' : '0' })}
                            />
                            <label className="form-check-label fs-11 text-muted" htmlFor="directBusinessStatusCheck">
                              Rule Mandatory
                            </label>
                          </div>
                        </div>

                        {/* Team Business */}
                        <div className="col-md-4 col-12">
                          <label className="form-label fs-12 fw-semibold">Team Business Required ($)</label>
                          <input
                            type="number"
                            min="0"
                            step="any"
                            className="form-control form-control-sm font-monospace"
                            placeholder="0"
                            value={formData.team_business}
                            onChange={(e) => setFormData({ ...formData, team_business: e.target.value })}
                          />
                          <div className="form-check form-switch mt-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="teamBusinessStatusCheck"
                              checked={formData.team_business_status === '1' || formData.team_business_status === 'active'}
                              onChange={(e) => setFormData({ ...formData, team_business_status: e.target.checked ? '1' : '0' })}
                            />
                            <label className="form-check-label fs-11 text-muted" htmlFor="teamBusinessStatusCheck">
                              Rule Mandatory
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer bg-light py-2 px-4 border-top d-flex justify-content-end gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowModal(false)}
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-sm btn-primary px-4 fw-semibold"
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <i className="fa fa-spinner fa-spin me-1"></i> Saving...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-check me-1"></i> {modalMode === 'create' ? 'Create Target Offer' : 'Save Changes'}
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* ==================================================== */}
        {/* DELETE CONFIRMATION MODAL */}
        {/* ==================================================== */}
        {showDeleteModal && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content border-0 shadow-lg rounded-3">
                <div className="modal-body p-4 text-center">
                  <div className="p-3 bg-danger-subtle text-danger rounded-circle d-inline-block mb-3">
                    <i className="fa fa-trash-alt fs-24"></i>
                  </div>
                  <h6 className="fw-bold text-dark mb-1">Delete Target Offer?</h6>
                  <p className="text-muted fs-13 mb-3">
                    Are you sure you want to delete <strong>"{rewardToDelete?.name}"</strong>? This will permanently remove the reward campaign.
                  </p>
                  <div className="d-flex justify-content-center gap-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-light border px-3"
                      onClick={() => setShowDeleteModal(false)}
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger px-3"
                      onClick={handleDeleteReward}
                      disabled={submitting}
                    >
                      {submitting ? 'Deleting...' : 'Yes, Delete'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================================================== */}
        {/* IMAGE PREVIEW LIGHTBOX MODAL */}
        {/* ==================================================== */}
        {previewImageModal && (
          <div
            className="modal show d-block"
            tabIndex="-1"
            style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
            onClick={() => setPreviewImageModal(null)}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content border-0 bg-transparent text-center">
                <div className="d-flex justify-content-end mb-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-light rounded-circle py-1 px-2"
                    onClick={() => setPreviewImageModal(null)}
                  >
                    <i className="fa fa-times"></i>
                  </button>
                </div>
                <img
                  src={previewImageModal}
                  alt="Full Flyer Preview"
                  className="img-fluid rounded shadow-lg mx-auto"
                  style={{ maxHeight: '80vh', objectFit: 'contain' }}
                />
              </div>
            </div>
          </div>
        )}

        <ToastContainer position="top-right" autoClose={3000} />
      </Header>
      <Footer />
    </>
  );
}

export default Rewards;
